import { useState } from 'react';
import {
  Page,
  PageBody,
  PageHeader,
  PageHeaderTitle,
  PageHeaderActions,
  Spinner,
  Stack,
  Banner,
  Text,
  InlineStack,
} from '@customerio/pluma-components/react';
import { Helmet } from 'react-helmet-async';
import { useQueryState, parseAsString } from 'nuqs';
import type { ServicesApiGetEnvHealthQueryParams } from '@/services/api/services-api';
import { servicesQuery } from '@/services/api';
import { getWorkspaceIdFromCurrentUrl } from '@/context/current-workspace';
import { HealthOverview } from './components/health-overview';

type SimulateValue = NonNullable<ServicesApiGetEnvHealthQueryParams['simulate']>;

export function HealthPage() {
  const isLocalDev = import.meta.env.DEV as boolean;
  const [simulate, setSimulate] = useState<string>('off');
  const simulateParam = simulate === 'off' ? undefined : (simulate as SimulateValue);
  const [activeTab, setActiveTab] = useQueryState('tab', parseAsString.withDefault('overview'));

  const workspaceId = getWorkspaceIdFromCurrentUrl();
  const healthQuery = servicesQuery.getEnvHealth.useQuery({
    queryKey: ['env-health', workspaceId, simulateParam],
    queryData: { params: { environment_id: workspaceId ?? 0 }, query: { simulate: simulateParam } },
    refetchInterval: 30_000,
    enabled: workspaceId != null,
  });
  const data = healthQuery.data?.body ?? null;

  return (
    <>
      <Helmet>
        <title>Workspace Performance | Customer.io</title>
      </Helmet>
      <Page isFullScreen w="full">
        <PageHeader>
          <PageHeaderTitle>Workspace Performance</PageHeaderTitle>
          {isLocalDev ? (
            <PageHeaderActions>
              <InlineStack gap="100" alignBlock="center">
                <Text size="xs" color="subtle">
                  Simulate
                </Text>
                <select
                  value={simulate}
                  onChange={(e) => setSimulate(e.target.value)}
                  style={{ fontSize: 12, padding: '2px 4px' }}
                >
                  <option value="off">Live</option>
                  <option value="healthy">Healthy</option>
                  <option value="busy">Busy</option>
                  <option value="degraded">Degraded</option>
                </select>
              </InlineStack>
            </PageHeaderActions>
          ) : null}
        </PageHeader>
        <PageBody>
          <Stack gap="400" pb="200">
            {healthQuery.isLoading ? <Spinner /> : null}

            {healthQuery.isError ? (
              <Banner variant="error" title="Error">
                Unable to load health data. Please try again later.
              </Banner>
            ) : null}

            {data != null ? (
              <HealthOverview
                data={data}
                simulate={simulateParam}
                activeTab={activeTab}
                onTabChange={(value) => void setActiveTab(value)}
              />
            ) : null}
          </Stack>
        </PageBody>
      </Page>
    </>
  );
}
